<template>
  <div>
    <b-form-group
      :label="`${$t('teaching.instructional.moduleTitleField')}:`"
      label-for="input-module-title"
      label-cols="4"
      label-cols-sm="3"
      class="label"
    >
      <b-form-input
        id="input-module-title"
        name="input-module-title"
        v-model="$v.module_form.title.$model"
        :state="validateState('title')"
        :placeholder="
          $t('teaching.instructional.moduleTitlePlaceholder', {
            entity: containerEntityName,
          })
        "
        aria-describedby="input-module-title-live-feedback"
        size="sm"
      ></b-form-input>
      <b-form-invalid-feedback id="input-module-title-live-feedback">
        {{ $t("reusable.common.minLength", { min: 3 }) }}
      </b-form-invalid-feedback>
    </b-form-group>

    <b-form-group
      :label="`${$t('teaching.instructional.moduleDescriptionField')}:`"
      label-cols="0"
      label-cols-sm="3"
      class="p-0"
    >
      <div
        class="form-description mb-2"
        @click="$bvModal.show(descriptionModalId)"
      >
        <RenderVHtml
          v-b-tooltip.v-secondary.noninteractive.hover="{
            title: $t('reusable.common.editDescription'),
            boundary: 'viewport',
          }"
          :content="module_form.description"
        ></RenderVHtml>
      </div>
      <ContainerRichTextKlv3
        :id_modal="descriptionModalId"
        :title_modal="$t('reusable.common.editDescription')"
        :size_modal="`lg`"
        :object_rich_text="module"
        :content_rich_text="module_form.description"
        :permit_blank="true"
        @save="onDescriptionSaved"
        @close="$bvModal.hide(descriptionModalId)"
      >
      </ContainerRichTextKlv3>
    </b-form-group>

    <p class="kl-hint">
      {{
        $t("teaching.instructional.moduleStudyUnitsAuto", {
          entity: containerEntityName,
          study_unit: $getVisibleNames(
            "teaching.studyunit",
            true,
            "Unidades de Estudio"
          ),
          indicator: $getVisibleNames(
            "teaching.evaluationcriteriamicro",
            true,
            "Indicadores de Logro"
          ),
        })
      }}
    </p>

    <div class="row mt-3">
      <div class="col" style="text-align: right">
        <b-button class="m-0 mr-1" size="sm" :disabled="saving" @click="save">
          {{ $t("reusable.common.save") }}
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import { toast } from "@/utils/utils";
import { validationMixin } from "vuelidate";
import { required, minLength } from "vuelidate/lib/validators";
import * as names from "@/store/names";

export default {
  name: "ModuleForm",
  mixins: [validationMixin],
  components: {
    RenderVHtml: () => import("@/components/reusable/RenderVHtml"),
    ContainerRichTextKlv3: () =>
      import("@/components/reusable/ContainerRichTextKlv3"),
  },
  props: {
    // null => creación.
    module: {
      type: Object,
      default: null,
    },
    instructional_design_id: {
      type: Number,
      required: true,
    },
    // Orden sugerido al crear: se ubica al final de los existentes.
    next_order: {
      type: Number,
      default: 1,
    },
  },
  data() {
    return {
      module_form: {
        title: this.module ? this.module.title : "",
        description: this.module ? this.module.description || "" : "",
      },
      saving: false,
    };
  },
  validations: {
    module_form: {
      title: {
        required,
        minLength: minLength(3),
      },
    },
  },
  computed: {
    containerEntityName() {
      return this.$getVisibleNames(
        "manual.instructional_design.container",
        false,
        "Módulo"
      );
    },
    descriptionModalId() {
      return `instructional-module-description-modal-${
        this.module ? this.module.id : "new"
      }`;
    },
  },
  methods: {
    validateState(name) {
      const { $dirty, $error } = this.$v.module_form[name];
      return $dirty ? !$error : null;
    },
    onDescriptionSaved(content) {
      // Sólo actualiza el borrador; se persiste al guardar el formulario.
      this.module_form.description = content;
      this.$bvModal.hide(this.descriptionModalId);
    },
    save() {
      this.$v.module_form.$touch();
      if (this.$v.module_form.$anyError) return;
      this.saving = true;
      const payload = {
        title: this.module_form.title.trim(),
        description: this.module_form.description,
      };
      const request = this.module
        ? this.$store.dispatch(names.PATCH_INSTRUCTIONAL_MODULE, {
            instructional_module_id: this.module.id,
            item: payload,
          })
        : this.$store.dispatch(names.POST_INSTRUCTIONAL_MODULE, {
            ...payload,
            order: this.next_order,
            instructional_design: this.instructional_design_id,
          });

      request
        .then((response) => {
          this.saving = false;
          toast(this.$t("teaching.instructional.saved"));
          this.$emit("saved", response);
        })
        .catch(() => {
          this.saving = false;
          toast(this.$t("teaching.instructional.saveError"));
        });
    },
  },
};
</script>

<style scoped>
.kl-hint {
  font-size: var(--kl-fs-small);
  color: #6b7b79;
  margin: 0;
}
.form-description {
  border: 1px solid var(--kl-table-border, #d9e0e0);
  border-radius: var(--kl-radius-md, 6px);
  padding: 0.35rem 0.55rem;
  min-height: 2.1rem;
  cursor: pointer;
  font-size: var(--kl-fs-body);
}
.form-description:hover {
  background-color: #f2f8f6;
}
</style>
